import React, { useEffect, useState } from "react";
import { Button, Col, Form } from "react-bootstrap";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import SpinnerNew from "~/components/SpinnerNew";
import ButtonAdd from "~/components/Buttons/AdicionarBtn";
import { withNamespaces } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getCvMgrConfiguration,
  persistCvMgrConfiguration,
  deleteCvMgrConfiguration,
} from "~/store/ducks/rhadminConfiguration/actionTypes";
import {
  generateCVPrompt,
  setGenerateCVPrompt,
} from "~/store/ducks/cvManagement/actionTypes";
import CvMgrDocumentConfiguration from "./CvMgrDocumentConfiguration";

const CVManagementConfiguration = ({ organizationId, t }) => {
  const dispatch = useDispatch();
  const {
    cvMgrConfiguration,
    successPersistCvMgrConfiguration,
    successDeleteCvMgrDocument,
  } = useSelector((state) => state.rhadminConfigurationReducer);
  const { isLoadingSaveCvMgrConfiguration, generateCVPromptResult } =
    useSelector((state) => state.openaiReducer);

  const [cvmgrApiKey, setCvmgrApiKey] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const [messageAlert, setMessageAlert] = useState("");
  const [severityMessage, setSeverityMessage] = useState("success");
  const [indexSelected, setIndexSelected] = useState(null);
  const [cvMgrConfigurationState, setCvMgrConfigurationState] = useState([]);

  useEffect(() => {
    setCvMgrConfigurationState([]);
    setCvmgrApiKey("");
    setIsEditing(false);
    setIndexSelected(null);
  }, []);

  useEffect(() => {
    if (generateCVPromptResult !== "") {
      handleUpdate(indexSelected, "prompt", generateCVPromptResult);
      dispatch(setGenerateCVPrompt(""));
      setIndexSelected(null);
    }
  }, [generateCVPromptResult, indexSelected]);

  useEffect(() => {
    if (organizationId) dispatch(getCvMgrConfiguration(organizationId));
  }, [organizationId]);

  useEffect(() => {
    if (cvMgrConfiguration.length > 0) {
      setCvMgrConfigurationState(cvMgrConfiguration.datasets || {});
      setCvmgrApiKey(cvMgrConfiguration.extractionApiKey);
    }
  }, [cvMgrConfiguration]);

  useEffect(() => {
    if (successPersistCvMgrConfiguration) setIsEditing(false);
    if (successDeleteCvMgrDocument) {
      setAlertModal(true);
      setMessageAlert("Documento eliminado com sucesso");
      setSeverityMessage("success");
    }
  }, [successPersistCvMgrConfiguration, successDeleteCvMgrDocument]);

  useEffect(() => {
    if (cvMgrConfigurationState.length > 0) {
      cvMgrConfigurationState.map((config, index) => {
        handleUpdate(index, "documentType", config.documentType);
        handleUpdate(index, "prompt", config.prompt);
        handleUpdate(index, "fields", config.fields);
        handleUpdate(index, "documentDescription", config.documentDescription);
      });
    }
  }, []);

  const handleSave = () => {
    const fieldsToCheck = [
      "documentType",
      "prompt",
      "fields",
      "documentDescription",
    ];
    const hasEmptyField = cvMgrConfigurationState.some((item) =>
      fieldsToCheck.some(
        (field) =>
          item[field] === "" ||
          item[field] === null ||
          item[field] === undefined
      )
    );

    if (hasEmptyField) {
      setAlertModal(true);
      setMessageAlert(
        "Preencha os campos obrigatórios (Tipo de Documento, Descrição do Documento, ID do Fluxo, Prompt e Campos)"
      );
      setSeverityMessage("error");
    } else {
      const documentTypes = cvMgrConfigurationState.map((item) =>
        item.documentType.trim()
      );
      const hasDuplicate = documentTypes.some((type, index) =>
        documentTypes.includes(type, index + 1)
      );

      if (hasDuplicate) {
        setAlertModal(true);
        setMessageAlert("Esse tipo de documento já existe");
        setSeverityMessage("error");
      } else {
        const payload = {
          id:
            cvMgrConfiguration && cvMgrConfiguration?.id
              ? cvMgrConfiguration.id
              : null,
          extractionApiKey: cvmgrApiKey,
          organization_id: organizationId,
          active: true,
          datasets: cvMgrConfigurationState,
        };
        dispatch(persistCvMgrConfiguration(payload));
      }
    }
  };

  const addNewDoc = () => {
    setCvMgrConfigurationState((configs) => [
      ...configs,
      {
        documentType: "",
        documentDescription: "",
        fields: "",
        prompt: "",
        active: true,
      },
    ]);
  };

  const deleteDoc = (docIndex) => {
    const newCvMgrConfigurationState = cvMgrConfigurationState.filter(
      (_, index) => index !== docIndex
    );

    setCvMgrConfigurationState(newCvMgrConfigurationState);
    dispatch(deleteCvMgrConfiguration(docIndex));
  };

  const handleUpdate = (index, key, value) => {
    setCvMgrConfigurationState((prevState) => {
      const newState = [...prevState];
      if (!newState[index]) newState[index] = {};
      newState[index][key] = value;
      return newState;
    });
  };

  const handlePromptButtonClick = (index) => {
    const item = cvMgrConfigurationState[index];

    const text = item.fields;

    const lines = text.split(";");
    const newItems = {};

    for (let line of lines) {
      const parts = line.split(":");
      if (parts.length !== 2) continue;

      const key = parts[0].trim();
      const value = parts[1].trim();

      if (value === "") continue;

      newItems[key] = value;
    }
    dispatch(generateCVPrompt(newItems, item.documentDescription));
    setIndexSelected(index);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertModal(false);
    setMessageAlert("");
  };

  const renderCVMgrDocumentConfiguration = () => {
    if(cvMgrConfigurationState.length === 0) {
      return (
        <div style={{ marginTop: "20px" }}>
          <p>Nenhuma configuração de documento encontrada.</p>
        </div>
      );
    }
    return (
      cvMgrConfigurationState &&
      cvMgrConfigurationState.map((config, index) => {
        return (
          <CvMgrDocumentConfiguration
            setIsEditing={setIsEditing}
            cvMgrConfigurationState={cvMgrConfigurationState[index]}
            handleUpdate={(key, value) => handleUpdate(index, key, value)}
            deleteDoc={deleteDoc}
            index={index}
            handlePromptButtonClick={handlePromptButtonClick}
          />
        );
      })
    );
  };

  return (
    <>
      <SpinnerNew open={isLoadingSaveCvMgrConfiguration} />
      <Snackbar
        open={alertModal}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={severityMessage}
          sx={{ width: "100%" }}
        >
          {messageAlert}
        </Alert>
      </Snackbar>
      <Form.Row style={{ margin: "20px" }}>
        <Col sm="12" md="12" lg="12">
          <Form.Group controlId="validationCustom">
            <Form.Label style={{ marginTop: "20px" }}>
              <h5>CV Mgr Configuração</h5>
            </Form.Label>
            <Stack spacing={2} direction="row">
              <Form.Group
                controlId="cvmgrApiKey"
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Form.Label>API KEY</Form.Label>
                <Form.Control
                  type="text"
                  style={{ marginLeft: "15px", width: "500px" }}
                  value={cvmgrApiKey}
                  onChange={(e) => {
                    setCvmgrApiKey(e.target.value);
                    setIsEditing(true);
                  }}
                  autoComplete="off"
                />
              </Form.Group>
              <ButtonAdd
                id={"button_admin_cvmgrConf_newDoc"}
                text="New cv type"
                onClick={addNewDoc}
              />
            </Stack>
            {renderCVMgrDocumentConfiguration()}

            {isEditing && (
              <Button
                id={"button_admin_cvmgrConf_saveConfig"}
                className="card-button"
                onClick={() => handleSave()}
              >
                {t("admin.organization.settings.saveConfig")}
              </Button>
            )}
          </Form.Group>
        </Col>
      </Form.Row>
    </>
  );
};

export default withNamespaces()(CVManagementConfiguration);
